<template>
  <ContentCard v-if="entityType === 'task'" width="100%" :source="item" />
  <v-card
    v-else
    class="card-hover px-4 py-3 d-flex align-center"
    style="position: relative"
    elevation="0"
    outlined
    @click="handleClick"
  >
    <div
      style="width:107px;
      height:107px"
      class="me-3"
      v-if="entityType !== 'routine'"
    >
      <ImageMedia
        class="rounded-sm"
        width="107px"
        height="107px"
        :src="item.image"
      />
    </div>
    <div
      class="d-flex flex-column"
      style="width: 100%; height: 100% !important"
    >
      <div class="d-flex align-center mb-3 ">
        <EntityIcon size="20" :entityType="entityType" class="me-1" />
        <div>
          <p
            class="ma-0 pa-0 blue-grey--text text--darken-3 font-size-10 font-weight-500"
          >
            {{ entityName }}
          </p>
          <p
            class="ma-0 pa-0 blue-grey--text text--darken-1 font-size-10 font-weight-400"
            v-snip="1"
          >
            {{ createdAt }}
          </p>
        </div>
      </div>
      <div class="d-flex flex-column justify-self-start flex-grow-1">
        <p
          class="font-size-14 font-weight-600 ma-0 pa-0 mb-2 blue-grey--text text--darken-2"
          v-snip="isRoutineType ? 2 : 1"
        >
          {{ name }}
        </p>
        <div class="mb-2" v-if="!pendingValidation && entityType !== 'routine'">
          <StatusChip :entity="item" />
        </div>
      </div>

      <div
        class="d-flex justify-space-between align-center"
        style="width: 100%"
        v-if="pendingValidation || dueDate || priority"
      >
        <div
          class="flex-grow-0 d-flex flex-column"
          style="height:20px"
          v-if="!pendingValidation && dueDate"
        >
          <p
            class="ma-0 pa-0 font-size-10 d-flex flex-grow-1 align-center due-date-container"
            :class="isOverdue ? 'red--text' : 'blue-grey--text text--darken-1'"
          >
            {{ $t('common.due') }}:
            {{ dueDateText }}
            <v-icon v-if="isOverdue" class="ml-1" size="16" color="red"
              >mdi-alert-circle-outline</v-icon
            >
          </p>
        </div>
        <div
          class="flex-grow-0 d-flex flex-column"
          style="height:20px"
          v-if="pendingValidation && requestedDateText"
        >
          <p
            class="mt-3 pa-0 font-size-10 d-flex flex-grow-1 align-center blue-grey--text text--darken-1 due-date-container"
          >
            {{ $t('common.requested') }}:
            {{ requestedDateText }}
            <v-icon v-if="isOverdue" class="ml-1" size="16" color="red"
              >mdi-alert-circle-outline</v-icon
            >
          </p>
        </div>

        <PriorityIcon
          v-if="priority"
          class="mr-2"
          :priority="priority"
          :class="pendingValidation ? 'mt-3' : ''"
        />
      </div>
      <div style="width: 100%" class="align-self-end">
        <div class="justify-self-end">
          <v-btn
            v-if="entityType === 'routine'"
            color="blue lighten-2"
            small
            block
            elevation="0"
            class="blue--text mt-2"
            rounded
            @click="resumeRoutineTrack"
            :to="{
              name: 'routineExecutionDetail',
              params: { routineId: item.id },
            }"
            >{{ $t('home.to_do.resume_routine') }}</v-btn
          >
        </div>
      </div>
    </div>
    <div
      v-if="pendingValidation"
      class="validation-tag py-1 px-2 d-flex justify-center align-left flex-column elevation-2"
    >
      <div class="d-flex justify-start align-center">
        <v-icon size="12" class="icon-background ml-1" color="white"
          >mdi-check-decagram-outline</v-icon
        >
        <span
          class="white--text font-size-8 font-weight-600 pa-0 my-0 ml-1 text-truncate"
        >
          {{ $t('rev.validation.validate') }}
        </span>
      </div>
      <p class="white--text font-size-8 pa-0 ml-1 my-0 text-truncate">
        {{ totalImpactText }}
      </p>
    </div>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import moment from 'moment'
import _ from 'lodash'

import trackingMixin from '@/mixins/tracking/trackerMixin.vue'
import revMixin from '@/mixins/revMixin'
import mixin from '@/mixins/mixin.vue'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'
import currencies from '@/locales/currencies.json'
import { utils } from 'frontend-js-shared'

import PriorityIcon from '@/components/PriorityIcon.vue'
import StatusChip from '@/components/Core/StatusChip.vue'
import ImageMedia from '@/components/ImageMedia/ImageMedia.vue'
import EntityIcon from '@/components/EntityIcon.vue'
import ContentCard from '@/components/ContentCard.vue'

export default {
  name: 'ToDoItemCard',

  mixins: [trackingMixin, mixin, revMixin],

  components: {
    PriorityIcon,
    StatusChip,
    ImageMedia,
    EntityIcon,
    ContentCard,
  },

  props: {
    item: {
      type: Object,
      required: true,
    },
    pendingValidation: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      showContentDrawer: false,
    }
  },

  computed: {
    ...mapGetters(['organizationId', 'organization']),

    createdAt() {
      return this.$t('home.created_at', {
        date: utils.dates.humanDate(this.item.createdAt),
      })
    },

    entityName() {
      switch (this.entityType) {
        case 'routine':
          return this.$t('routines.routine_one')
        case 'finding':
          return this.$t('routines.finding_one')
        case 'challenge':
          return this.$t('common.challenge_one')
        case 'task':
          return this.$t('common.task_one')
        default:
          return (
            this.organization?.customNamings?.[this.entityType]?.singular ||
            this.$t(`common.rev_type.${_.snakeCase(this.entityType)}_one`) ||
            ''
          )
      }
    },

    name() {
      return this.item.name
    },

    isRoutineType() {
      return this.entityType === 'routine'
    },

    priority() {
      return this.item?.priority ?? this.item?.metadata?.priority ?? ''
    },

    entityType() {
      return this.item?.entityType ?? this.item?.type
    },

    dueDate() {
      return this.item?.dueDate
    },

    requestedDate() {
      if (this.pendingValidation) {
        return (
          this.item?.metadata?.validationRequest?.find(
            request => request.status === 'inProgress'
          )?.createdAt ?? ''
        )
      }
      return ''
    },

    dueDateText() {
      return utils.dates.humanDueDate(this.dueDate) ?? ''
    },

    requestedDateText() {
      return utils.dates.humanDueDate(this.requestedDate) ?? ''
    },

    isOverdue() {
      const today = moment()
        .add(0, 'days')
        .format('YYYY-MM-DD')
      const dueDate = moment(this.dueDate).format('YYYY-MM-DD')
      return dueDate < today
    },

    currencySymbol() {
      return _.get(currencies[this.revCurrency], 'symbol_native', '')
    },

    totalImpactText() {
      const totalImpact =
        this.item?.metadata?.validatedReview?.totalImpact ?? null
      const currency =
        this.item?.metadata?.currencyMetadata?.currentCurrency ||
        this.item?.metadata?.currencyMetadata?.globalCurrency

      const currencySymbol = _.get(currencies[currency], 'symbol_native', '')

      return totalImpact
        ? `
            ${currencySymbol}
            ${Number(totalImpact).toLocaleString(navigator.language)}
            ${currency}
            `
        : '-'
    },
  },

  methods: {
    trackHomeToDo() {
      switch (this.item.type) {
        case this.$constants.exploreEntityTypes.finding:
          this.trackHomeNavigate({ section: 'toDo', element: 'finding' })
          break
        case this.$constants.exploreEntityTypes.routine:
          this.trackHomeNavigate({ section: 'toDo', element: 'routine' })
          break
        default:
          this.trackHomeNavigate({ section: 'toDo', element: 'rev' })
          break
      }
    },

    goToValidation() {
      this.$router.replace({
        path: `validate/${_.kebabCase(this.item.type)}/${this.item.id}`,
      })
    },

    handleClick() {
      if (this.pendingValidation) {
        return this.goToValidation()
      } else {
        this.openEntityDrawer()
      }
      this.trackHomeToDo()
    },

    openEntityDrawer() {
      eventHub.$emit(HUB_EVENTS.ENTITY_DRAWER.UPDATE, this.item)
    },

    getItem(item) {
      switch (this.item.entityType) {
        case 'finding':
          return {
            ...this.item,
            ...item,
            createdBy: this.item.createdBy,
            name: item.situation,
          }
        case 'task':
          return {
            ...this.item,
            ...item,
            entityType: 'task',
            linkedEntityType: item.linkedEntityType ?? item.entityType,
            linkedEntityId: item.linkedEntityId ?? item.entityId,
          }
        default:
          return { ...this.item, ...item }
      }
    },

    resumeRoutineTrack() {
      this.trackHomeNavigate({ section: 'toDo', element: 'resumeRoutine' })
    },
  },
}
</script>
<style scoped lang="scss">
.validation-tag {
  width: 100px;
  height: 40px;
  background-color: $color-purple;
  border-top-right-radius: 8px !important;
  border-bottom-right-radius: 8px !important;
  border-bottom-left-radius: 0px !important;
  border-top-left-radius: 0px !important;
  position: absolute;
  left: -5px;
  bottom: 8px;
}
.icon-background {
  background-color: $color-purple-lighten1;
  border-radius: 50%;
  padding: 2px;
  width: fit-content;
  height: fit-content;
}
</style>
